// Routine invoeren of wijzigen (spec 4.4): vaste dagen, een aantal keer per week, of af en toe.
import { useState, useSyncExternalStore, type FormEvent } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { toast } from "sonner";
import { db, type Routine } from "../lib/db";
import { archiveRoutine, saveRoutine, type RoutineForm } from "../lib/routines";
import { Sheet } from "./Sheet";
import { Button, cn } from "./ui";
import { intervalDays, intervalParts, type IntervalUnit } from "@shared/routines";

type Kind = RoutineForm["slotKinds"][number];

type OpenState = { kind: "new"; scheduleType: Routine["schedule_type"] } | { kind: "edit"; id: string };

let state: OpenState | null = null;
const listeners = new Set<() => void>();
export function openRoutine(next: OpenState | null) {
  state = next;
  listeners.forEach((l) => l());
}
function useOpen() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}

export const WEEKDAY_SHORT = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const DURATIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];

/**
 * The kinds of block a routine can go in, named after the blocks in PK's own rhythm ("Douchen", "Vrij"),
 * so nobody has to learn the word "slot". Steps of a group and work blocks are no place for a routine.
 */
export function useSlotOptions(): { kind: Kind; label: string; short: string }[] {
  return (
    useLiveQuery(async () => {
      const templates = new Set((await db.day_templates.toArray()).filter((t) => !t.deleted_at && !t.archived_at).map((t) => t.id));
      const blocks = (await db.day_template_blocks.toArray()).filter(
        (b) => !b.deleted_at && templates.has(b.template_id) && !b.group_title && b.slot_kind !== "work",
      );
      // Per kind the block titles, most used first: "Sport of Bewegen"; in short the most used one.
      const byKind = new Map<Kind, Map<string, number>>();
      for (const b of blocks) {
        const titles = byKind.get(b.slot_kind as Kind) ?? new Map<string, number>();
        byKind.set(b.slot_kind as Kind, titles.set(b.title, (titles.get(b.title) ?? 0) + 1));
      }
      return [...byKind]
        .map(([kind, titles]) => {
          const names = [...titles].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "nl")).map(([t]) => t);
          return { kind, label: names.join(" of "), short: names[0]! };
        })
        .sort((a, b) => a.label.localeCompare(b.label, "nl"));
    }) ?? []
  );
}

/** "Ongeveer elke 6 maanden": a number and a unit, stored as days (golf 9). */
function IntervalField({ days, onChange, inputClass }: { days: number; onChange: (days: number) => void; inputClass: string }) {
  const [parts, setParts] = useState(() => intervalParts(days));
  const max = { days: 365, weeks: 52, months: 12 }[parts.unit];
  const update = (next: { n: number; unit: IntervalUnit }) => {
    setParts(next);
    onChange(intervalDays(next.n, next.unit));
  };
  return (
    <fieldset className="col-span-2 flex flex-col gap-1 sm:col-span-1">
      <legend className="mb-1 text-sm text-ink-soft">Ongeveer elke</legend>
      <div className="flex gap-2">
        <input
          type="number"
          min={1}
          max={max}
          aria-label="Aantal"
          value={parts.n}
          onChange={(e) => update({ ...parts, n: Math.min(max, Math.max(1, Number(e.target.value) || 1)) })}
          className={cn(inputClass, "w-20 min-w-0")}
        />
        <select
          aria-label="Eenheid"
          value={parts.unit}
          onChange={(e) => {
            const unit = e.target.value as IntervalUnit;
            update({ unit, n: Math.min(parts.n, { days: 365, weeks: 52, months: 12 }[unit]) });
          }}
          className={cn(inputClass, "min-w-0 flex-1")}
        >
          <option value="days">dagen</option>
          <option value="weeks">weken</option>
          <option value="months">maanden</option>
        </select>
      </div>
    </fieldset>
  );
}

function durationLabel(m: number) {
  if (m < 60) return `${m} minuten`;
  return m === 60 ? "1 uur" : m % 60 ? `${Math.floor(m / 60)} uur ${m % 60} min` : `${m / 60} uur`;
}

function formOf(r: Routine): RoutineForm {
  return {
    title: r.title,
    scheduleType: r.schedule_type,
    weekdays: r.weekdays,
    timesPerWeek: r.times_per_week,
    intervalDays: r.interval_days,
    slotKinds: r.slot_kinds.filter((k): k is Kind => k !== "work"),
    durationMinutes: r.duration_minutes,
    weeklyTarget: r.weekly_target,
    isOptional: r.is_optional,
    lastDoneOn: r.last_done_on,
  };
}

function emptyForm(scheduleType: Routine["schedule_type"]): RoutineForm {
  return {
    title: "",
    scheduleType,
    weekdays: [],
    timesPerWeek: scheduleType === "cadence" ? 2 : null,
    intervalDays: scheduleType === "occasional" ? 14 : null,
    slotKinds: [],
    durationMinutes: 15,
    weeklyTarget: null,
    isOptional: false,
    lastDoneOn: null,
  };
}

export function RoutineSheetHost() {
  const open = useOpen();
  const routine = useLiveQuery(async () => (open?.kind === "edit" ? ((await db.routines.get(open.id)) ?? null) : null), [open]);
  // A live query keeps its previous result while the new one loads: wait for this very routine.
  if (!open || (open.kind === "edit" && routine?.id !== open.id)) return null;
  const key = open.kind === "edit" ? open.id : `new-${open.scheduleType}`;
  const current = open.kind === "edit" ? routine! : null;
  return (
    <RoutineSheet
      key={key}
      routine={current}
      initial={current ? formOf(current) : emptyForm(open.kind === "new" ? open.scheduleType : "fixed")}
    />
  );
}

function RoutineSheet({ routine, initial }: { routine: Routine | null; initial: RoutineForm }) {
  const [form, setForm] = useState<RoutineForm>(initial);
  const [error, setError] = useState<string | null>(null);
  const slots = useSlotOptions();
  const set = <K extends keyof RoutineForm>(key: K, value: RoutineForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const close = () => openRoutine(null);
  const input = "min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) return setError("Geef de routine een naam.");
    if (form.scheduleType === "fixed" && !form.weekdays.length) return setError("Kies minstens één dag.");
    if (form.weeklyTarget && form.scheduleType === "fixed" && form.weeklyTarget > form.weekdays.length) {
      return setError("De weekteller kan niet hoger zijn dan het aantal dagen.");
    }
    await saveRoutine(form, routine?.id);
    toast(routine ? "Routine bewaard" : "Routine toegevoegd");
    close();
  }

  async function remove() {
    if (!routine || !window.confirm(`${routine.title} verwijderen? Wat al op een dag staat die al gepland is, blijft staan.`)) return;
    await archiveRoutine(routine.id);
    toast(`${routine.title} verwijderd`);
    close();
  }

  const types = [
    ["fixed", "Vaste dagen"],
    ["cadence", "Per week"],
    ["occasional", "Af en toe"],
  ] as const;

  return (
    <Sheet open onOpenChange={(o) => !o && close()} title={routine ? routine.title : "Nieuwe routine"}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="font-semibold">Naam</span>
          <input value={form.title} maxLength={200} onChange={(e) => set("title", e.target.value)} className={input} autoFocus={!routine} />
        </label>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 font-semibold">Wanneer</legend>
          <div className="grid grid-cols-3 gap-1">
            {types.map(([type, label]) => (
              <button
                key={type}
                type="button"
                aria-pressed={form.scheduleType === type}
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    scheduleType: type,
                    timesPerWeek: type === "cadence" ? (f.timesPerWeek ?? 2) : f.timesPerWeek,
                    intervalDays: type === "occasional" ? (f.intervalDays ?? 14) : f.intervalDays,
                  }))
                }
                className={cn(
                  "min-h-12 rounded-xl border-2 px-1",
                  form.scheduleType === type ? "border-pine bg-pine text-paper" : "border-mist",
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {form.scheduleType === "fixed" && (
            <>
              <div className="grid grid-cols-7 gap-0.5" role="group" aria-label="Dagen">
                {WEEKDAY_SHORT.map((d, i) => {
                  const on = form.weekdays.includes(i + 1);
                  return (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={on}
                      onClick={() => set("weekdays", on ? form.weekdays.filter((x) => x !== i + 1) : [...form.weekdays, i + 1])}
                      className={cn("min-h-12 rounded-lg border-2", on ? "border-pine bg-pine text-paper" : "border-mist")}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-ink-soft">Weekteller (zoals Gym 1 van 3)</span>
                <select
                  value={form.weeklyTarget ?? ""}
                  onChange={(e) => set("weeklyTarget", e.target.value ? Number(e.target.value) : null)}
                  className={input}
                >
                  <option value="">Geen</option>
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <option key={n} value={n}>
                      {n} keer per week
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex min-h-12 items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.isOptional}
                  onChange={(e) => set("isOptional", e.target.checked)}
                  className="size-6 accent-pine"
                />
                <span>Optioneel: telt nooit als gemist</span>
              </label>
            </>
          )}

          {form.scheduleType === "cadence" && (
            <label className="flex flex-col gap-1">
              <span className="text-sm text-ink-soft">Hoe vaak per week</span>
              <select value={form.timesPerWeek ?? 2} onChange={(e) => set("timesPerWeek", Number(e.target.value))} className={input}>
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <option key={n} value={n}>
                    {n === 1 ? "1 keer" : `${n} keer`}
                  </option>
                ))}
              </select>
            </label>
          )}

          {form.scheduleType === "occasional" && (
            <div className="grid grid-cols-2 gap-2">
              <IntervalField days={form.intervalDays ?? 14} onChange={(d) => set("intervalDays", d)} inputClass={input} />
              <label className="flex flex-col gap-1">
                <span className="text-sm text-ink-soft">Laatst gedaan</span>
                <input
                  type="date"
                  value={form.lastDoneOn ?? ""}
                  onChange={(e) => set("lastDoneOn", e.target.value || null)}
                  className={input}
                />
              </label>
            </div>
          )}
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 font-semibold">In welk blok</legend>
          <p className="text-sm text-ink-soft">
            {form.scheduleType === "occasional"
              ? "Waar het past als je het inplant."
              : "De app zet het in het eerste blok dat past. Niets gekozen: het staat bij Vandaag ergens."}
          </p>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Blokken">
            {slots.map(({ kind, label }) => {
              const on = form.slotKinds.includes(kind);
              return (
                <button
                  key={kind}
                  type="button"
                  aria-pressed={on}
                  onClick={() => set("slotKinds", on ? form.slotKinds.filter((k) => k !== kind) : [...form.slotKinds, kind])}
                  className={cn("min-h-11 rounded-xl border-2 px-3", on ? "border-pine bg-pine text-paper" : "border-mist")}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1">
          <span className="font-semibold">Duur</span>
          <select value={form.durationMinutes} onChange={(e) => set("durationMinutes", Number(e.target.value))} className={input}>
            {[...new Set([...DURATIONS, form.durationMinutes])]
              .sort((a, b) => a - b)
              .map((m) => (
                <option key={m} value={m}>
                  {durationLabel(m)}
                </option>
              ))}
          </select>
        </label>

        {error && (
          <p role="alert" className="font-semibold text-ink">
            {error}
          </p>
        )}
        <Button type="submit">Opslaan</Button>
        {routine && (
          <Button type="button" variant="quiet" onClick={() => void remove()}>
            Routine verwijderen
          </Button>
        )}
      </form>
    </Sheet>
  );
}
